import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

// LocalStorage keys
const TOKEN_KEY = "gbe_token";              // the token currently being sent to the server
const ADMIN_TOKEN_KEY = "gbe_admin_token";  // stashed original admin token while impersonating

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [pending2FA, setPending2FA] = useState(null); // { temp_token, email }

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[Auth] fetchMe failed", e.response.status, e?.message || e);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) {
        setPending2FA({ temp_token: data.temp_token, email });
        return { requires_2fa: true };
      }
      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data);
      return { requires_2fa: false, must_change_password: !!data.must_change_password };
    } catch (e) {
      throw new Error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const verify2FA = async (code) => {
    if (!pending2FA) throw new Error("No pending 2FA session");
    try {
      const { data } = await api.post("/auth/2fa/verify", {
        temp_token: pending2FA.temp_token,
        code,
      });
      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data);
      setPending2FA(null);
      return { must_change_password: !!data.must_change_password };
    } catch (e) {
      throw new Error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
      setUser(data);
    } catch (e) {
      throw new Error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("[Auth] logout request failed", e?.message || e);
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(false);
    setPending2FA(null);
  };

  const cancelPending2FA = () => setPending2FA(null);

  // -----------------------------------------------------------
  // Impersonation
  // -----------------------------------------------------------
  const startImpersonation = async (userId) => {
    const { data } = await api.post("/admin/impersonate/start", { user_id: userId });
    // Stash the admin's own token so we can restore it on stop (or reload after)
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (currentToken) localStorage.setItem(ADMIN_TOKEN_KEY, currentToken);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    await fetchMe();
    return data;
  };

  const stopImpersonation = async () => {
    let restoredToken = null;
    try {
      const { data } = await api.post("/admin/impersonate/stop");
      if (data?.access_token) restoredToken = data.access_token;
    } catch (e) {
      console.warn("[Auth] stopImpersonation failed", e?.message || e);
    }
    // Prefer the fresh token returned by the server; fall back to the stashed one
    if (!restoredToken) restoredToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (restoredToken) {
      localStorage.setItem(TOKEN_KEY, restoredToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    await fetchMe();
  };

  const isImpersonating = !!user?.impersonation;

  return (
    <AuthCtx.Provider
      value={{
        user,
        pending2FA,
        login,
        verify2FA,
        register,
        logout,
        refresh: fetchMe,
        cancelPending2FA,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
